import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { approvalAPI, featureAPI, projectLinkAPI, taskBatchAPI, stageGateAPI, qaTestPlanAPI } from '../../api/services';
import { useAppSelector } from '../../store/hooks';

const FeatureApprovalAdmin = React.lazy(() => import('../Admin/ProjectGovernance/FeatureApprovalAdmin'));
const LinkRequestAdmin = React.lazy(() => import('../Admin/ProjectGovernance/LinkRequestAdmin'));
const BatchApprovalAdmin = React.lazy(() => import('../Admin/ProjectGovernance/BatchApprovalAdmin'));
const TestPlanApprovalAdmin = React.lazy(() => import('../Admin/ProjectGovernance/TestPlanApprovalAdmin'));

type ApprovalTab = 'features' | 'projects' | 'collaboration' | 'links' | 'batches' | 'gates' | 'testing';
const TABS: { key: ApprovalTab; label: string }[] = [
  { key: 'features', label: 'Features' },
  { key: 'batches', label: 'Batches' },
  { key: 'testing', label: 'Testing' },
  { key: 'gates', label: 'Stage Gates' },
  { key: 'projects', label: 'Projects' },
  { key: 'collaboration', label: 'Collaboration' },
  { key: 'links', label: 'Project Links' },
];

const STATUS_PILL: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700', in_progress: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};
const URGENCY_DOT: Record<string, string> = {
  low: 'bg-gray-400', medium: 'bg-blue-500', high: 'bg-orange-500', critical: 'bg-red-500',
};

const Spinner = () => (
  <div className="flex items-center justify-center py-20">
    <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
  </div>
);

interface ChainItem {
  approverId: string; approverName: string; approverRoleName: string;
  status: string; forwardedToName?: string; comments?: string; actionAt?: string;
}
interface Approval {
  _id: string; projectName: string; requestedByName: string;
  requestedDepartmentName: string; targetDepartmentName?: string;
  approvalType: string; approvalChain: ChainItem[];
  status: string; requiresHigherApproval: boolean;
  shareableToken?: string; requestReason?: string; urgency: string;
  createdAt: string;
}

// ─── Main ────────────────────────────────────────────────────────────────────

const ApprovalsDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as ApprovalTab | null;
  const [activeTab, setActiveTab] = useState<ApprovalTab>(
    tabParam && TABS.some(t => t.key === tabParam) ? tabParam : 'features'
  );
  const [counts, setCounts] = useState<Record<string, number>>({});

  const fetchCounts = useCallback(async () => {
    try {
      const [collabRes, featureRes, linkRes, batchRes, gateRes, testingRes] = await Promise.allSettled([
        approvalAPI.getMyApprovals(),
        featureAPI.getPendingApprovals(),
        projectLinkAPI.getAllPending(),
        taskBatchAPI.getPending(),
        stageGateAPI.getPending(),
        qaTestPlanAPI.getPendingApprovals(),
      ]);
      const allApprovals: Approval[] = collabRes.status === 'fulfilled' ? (collabRes.value.data.data || []) : [];
      const pending = allApprovals.filter(a => a.status === 'pending' || a.status === 'in_progress');
      setCounts({
        features: featureRes.status === 'fulfilled' ? (featureRes.value.data.data || []).length : 0,
        batches: batchRes.status === 'fulfilled' ? (batchRes.value.data.data || []).length : 0,
        gates: gateRes.status === 'fulfilled' ? (gateRes.value.data.data || []).length : 0,
        testing: testingRes.status === 'fulfilled' ? (testingRes.value.data.data || []).length : 0,
        projects: pending.filter(a => a.approvalType === 'general').length,
        collaboration: pending.filter(a => a.approvalType !== 'general').length,
        links: linkRes.status === 'fulfilled' ? (linkRes.value.data.data || []).filter((l: any) => l.status === 'pending').length : 0,
      });
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const handleTabChange = (tab: ApprovalTab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  return (
    <div>
      {/* Tabs — matches RoleManagement / People pattern */}
      <div className="border-b border-gray-200 px-6">
        <div className="flex gap-0 -mb-px">
          {TABS.map(t => {
            const count = counts[t.key] || 0;
            return (
              <button key={t.key} onClick={() => handleTabChange(t.key)}
                className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                  activeTab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {t.label}
                {count > 0 && (
                  <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                    activeTab === t.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                  }`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === 'features' && (
        <React.Suspense fallback={<Spinner />}><FeatureApprovalAdmin onApprovalChange={fetchCounts} /></React.Suspense>
      )}
      {activeTab === 'batches' && (
        <React.Suspense fallback={<Spinner />}><BatchApprovalAdmin onApprovalChange={fetchCounts} /></React.Suspense>
      )}
      {activeTab === 'projects' && <ApprovalTable type="general" onRefresh={fetchCounts} />}
      {activeTab === 'collaboration' && <ApprovalTable type="collaboration" onRefresh={fetchCounts} />}
      {activeTab === 'testing' && (
        <React.Suspense fallback={<Spinner />}><TestPlanApprovalAdmin onApprovalChange={fetchCounts} /></React.Suspense>
      )}
      {activeTab === 'gates' && <StageGateTable onRefresh={fetchCounts} />}
      {activeTab === 'links' && (
        <React.Suspense fallback={<Spinner />}><LinkRequestAdmin onApprovalChange={fetchCounts} /></React.Suspense>
      )}
    </div>
  );
};

// ─── Stage Gate Table ────────────────────────────────────────────────────────

const StageGateTable: React.FC<{ onRefresh: () => void }> = ({ onRefresh }) => {
  const { user: gateUser } = useAppSelector(s => s.auth);
  const gateUserId = gateUser?.id || (gateUser as any)?._id;
  const gateOrgPriority = (gateUser as any)?.organizationRole?.priority || 0;
  const [gates, setGates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedGateIds, setSelectedGateIds] = useState<Set<string>>(new Set());
  const [batchGateLoading, setBatchGateLoading] = useState(false);
  const [batchGateMsg, setBatchGateMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchGates = useCallback(async () => {
    try {
      const res = await stageGateAPI.getPending();
      setGates(res.data.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchGates(); }, [fetchGates]);

  const approvableGates = gates.filter((g: any) => {
    const isSelf = g.requestedBy?.userId && gateUserId && String(g.requestedBy.userId) === String(gateUserId) && gateOrgPriority < 50;
    return !isSelf;
  });

  const toggleGateSelect = (id: string) => {
    setSelectedGateIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleGateSelectAll = () => {
    const ids = approvableGates.map((g: any) => g._id);
    if (ids.every((id: string) => selectedGateIds.has(id))) {
      setSelectedGateIds(new Set());
    } else {
      setSelectedGateIds(new Set(ids));
    }
  };

  const handleBatchGateApprove = async () => {
    if (selectedGateIds.size === 0) return;
    setBatchGateLoading(true);
    setBatchGateMsg(null);
    const results = await Promise.allSettled(
      Array.from(selectedGateIds).map(id => stageGateAPI.approve(id))
    );
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    setSelectedGateIds(new Set());
    await fetchGates();
    onRefresh();
    setBatchGateLoading(false);
    if (failed === 0) {
      setBatchGateMsg({ type: 'success', text: `${succeeded} gate${succeeded !== 1 ? 's' : ''} approved` });
    } else {
      setBatchGateMsg({ type: 'error', text: `${succeeded} approved, ${failed} failed` });
    }
    setTimeout(() => setBatchGateMsg(null), 4000);
  };

  const handleAction = async (id: string, action: 'approve' | 'reject') => {
    setActionId(id);
    try {
      if (action === 'approve') await stageGateAPI.approve(id);
      else await stageGateAPI.reject(id, 'Rejected from approvals dashboard');
      fetchGates();
      onRefresh();
    } catch (err: any) { setError(err?.response?.data?.message || `Failed to ${action}`); }
    setActionId(null);
  };

  if (loading) return <Spinner />;

  return (
    <div className="px-6 py-4">
      {error && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 shrink-0">&times;</button>
        </div>
      )}
      {batchGateMsg && (
        <div className={`mb-3 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${batchGateMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {batchGateMsg.text}
        </div>
      )}
      {selectedGateIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
          <span className="text-xs font-medium text-blue-700">{selectedGateIds.size} selected</span>
          <button onClick={handleBatchGateApprove} disabled={batchGateLoading}
            className="text-xs bg-green-600 text-white px-4 py-1.5 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors">
            {batchGateLoading ? 'Approving...' : `Approve Selected (${selectedGateIds.size})`}
          </button>
          <button onClick={() => setSelectedGateIds(new Set())} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>
        </div>
      )}
      {gates.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-400">No pending stage gate requests</div>
      ) : (
        <div className="space-y-2">
          {approvableGates.length > 0 && (
            <div className="flex items-center gap-2 mb-1 px-1">
              <input type="checkbox"
                checked={approvableGates.length > 0 && approvableGates.every((g: any) => selectedGateIds.has(g._id))}
                onChange={toggleGateSelectAll}
                className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer" />
              <span className="text-xs text-gray-400">Select all</span>
            </div>
          )}
          {gates.map((g: any) => {
            const isSelf = g.requestedBy?.userId && gateUserId && String(g.requestedBy.userId) === String(gateUserId) && gateOrgPriority < 50;
            return (
            <div key={g._id} className={`flex items-center justify-between p-3 bg-white border rounded-lg ${selectedGateIds.has(g._id) ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3 min-w-0">
                {!isSelf && (
                  <input type="checkbox"
                    checked={selectedGateIds.has(g._id)}
                    onChange={() => toggleGateSelect(g._id)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer shrink-0" />
                )}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${g.gateType === 'approval' ? 'bg-amber-50' : 'bg-blue-50'}`}>
                  {g.gateType === 'approval' ? (
                    <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                    </svg>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-gray-900 truncate">
                    {g.gateType === 'approval' ? 'Approval' : 'Testing'} gate for <span className="font-medium">"{g.stage}"</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    Requested by {g.requestedBy?.name} {g.notes ? `— ${g.notes}` : ''}
                    {g.mandatory && <span className="text-red-500 ml-1">(mandatory)</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isSelf ? (
                  <span className="text-xs text-gray-400 italic">You cannot approve your own request</span>
                ) : (<>
                <button
                  onClick={() => handleAction(g._id, 'reject')}
                  disabled={actionId === g._id}
                  className="text-xs text-red-600 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-30"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleAction(g._id, 'approve')}
                  disabled={actionId === g._id}
                  className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-30"
                >
                  Approve
                </button>
                </>)}
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Shared Approval Table (Projects + Collaboration) ────────────────────────

const ApprovalTable: React.FC<{ type: 'general' | 'collaboration'; onRefresh: () => void }> = ({ type, onRefresh }) => {
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [selected, setSelected] = useState<Approval | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [comments, setComments] = useState('');
  const [showEscalate, setShowEscalate] = useState(false);
  const [escalationReason, setEscalationReason] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchMsg, setBatchMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isCollab = type === 'collaboration';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await approvalAPI.getMyApprovals();
      const all: Approval[] = res.data.data || [];
      setApprovals(isCollab
        ? all.filter(a => a.approvalType !== 'general')
        : all.filter(a => a.approvalType === 'general')
      );
    } catch { setError('Failed to load approvals'); }
    setLoading(false);
  }, [isCollab]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await approvalAPI.approveCollaboration(selected._id, comments);
      setSelected(null); setComments(''); load(); onRefresh();
    } catch (e: any) { setError(e.response?.data?.message || 'Failed'); setTimeout(() => setError(''), 4000); }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await approvalAPI.rejectCollaboration(selected._id, comments);
      setSelected(null); setComments(''); load(); onRefresh();
    } catch (e: any) { setError(e.response?.data?.message || 'Failed'); setTimeout(() => setError(''), 4000); }
    setActionLoading(false);
  };

  const handleEscalate = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await approvalAPI.escalateToHigherAuthority(selected._id, { escalationReason });
      setShowEscalate(false); setSelected(null); setEscalationReason(''); load(); onRefresh();
    } catch (e: any) { setError(e.response?.data?.message || 'Failed'); setTimeout(() => setError(''), 4000); }
    setActionLoading(false);
  };

  const pendingApprovals = approvals.filter(a => a.status === 'pending' || a.status === 'in_progress');

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pendingIds = pendingApprovals.map(a => a._id);
    if (pendingIds.every(id => selectedIds.has(id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingIds));
    }
  };

  const handleBatchApprove = async () => {
    if (selectedIds.size === 0) return;
    setBatchLoading(true);
    setBatchMsg(null);
    const results = await Promise.allSettled(
      Array.from(selectedIds).map(id => approvalAPI.approveCollaboration(id, ''))
    );
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    setSelectedIds(new Set());
    await load();
    onRefresh();
    setBatchLoading(false);
    if (failed === 0) {
      setBatchMsg({ type: 'success', text: `${succeeded} approved` });
    } else {
      setBatchMsg({ type: 'error', text: `${succeeded} approved, ${failed} failed` });
    }
    setTimeout(() => setBatchMsg(null), 4000);
  };

  const filtered = approvals.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'pending') return a.status === 'pending' || a.status === 'in_progress';
    return a.status === filter;
  });

  if (loading) return <Spinner />;

  return (
    <div>
      {error && <div className="mx-6 mt-3 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>}

      {/* Batch toast */}
      {batchMsg && (
        <div className={`mx-6 mt-3 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${batchMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {batchMsg.text}
        </div>
      )}

      {/* Batch action bar */}
      {selectedIds.size > 0 && (
        <div className="mx-6 mt-3 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
          <span className="text-xs font-medium text-blue-700">{selectedIds.size} selected</span>
          <button onClick={handleBatchApprove} disabled={batchLoading}
            className="text-xs bg-green-600 text-white px-4 py-1.5 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors">
            {batchLoading ? 'Approving...' : `Approve Selected (${selectedIds.size})`}
          </button>
          <button onClick={() => setSelectedIds(new Set())} className="text-xs text-gray-500 hover:text-gray-700 transition-colors">
            Clear
          </button>
        </div>
      )}

      {/* Summary + filter bar — matches workload pattern */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-gray-900">{approvals.length}</span>
          <span className="text-[10px] text-gray-400">{isCollab ? 'Requests' : 'Projects'}</span>
        </div>
        {[
          { key: 'all' as const, label: 'All', dot: 'bg-gray-400', color: 'text-gray-900' },
          { key: 'pending' as const, label: 'Pending', dot: 'bg-yellow-500', color: 'text-yellow-700' },
          { key: 'approved' as const, label: 'Approved', dot: 'bg-green-500', color: 'text-green-700' },
          { key: 'rejected' as const, label: 'Rejected', dot: 'bg-red-500', color: 'text-red-600' },
        ].map(f => {
          const count = f.key === 'all' ? approvals.length
            : f.key === 'pending' ? approvals.filter(a => a.status === 'pending' || a.status === 'in_progress').length
            : approvals.filter(a => a.status === f.key).length;
          if (count === 0 && f.key !== 'all') return null;
          return (
            <button key={f.key} onClick={() => setFilter(filter === f.key ? 'all' : f.key)}
              className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full transition-colors ${filter === f.key ? 'bg-gray-100 ring-1 ring-offset-1 ring-gray-300' : 'hover:bg-gray-50'}`}>
              <div className={`w-2 h-2 rounded-full ${f.dot}`} />
              <span className={`text-sm font-semibold ${filter === f.key ? f.color : 'text-gray-500'}`}>{count}</span>
              <span className="text-[10px] text-gray-400">{f.label}</span>
            </button>
          );
        })}
        {filter !== 'all' && (
          <>
            <div className="w-px h-4 bg-gray-200" />
            <button onClick={() => setFilter('all')} className="text-[10px] text-gray-400 hover:text-gray-600">Clear filter</button>
          </>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium text-gray-700">No {isCollab ? 'collaboration' : 'project'} approvals</p>
          <p className="text-xs text-gray-400 mt-1">{isCollab ? 'Department collaboration requests will appear here' : 'Project submissions for approval will appear here'}</p>
        </div>
      ) : (
        <div>
          {/* Grid header — matches grid-cols-12 pattern from Roles/Users */}
          <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
            <div className="col-span-3 flex items-center gap-2">
              {pendingApprovals.length > 0 && (
                <input type="checkbox"
                  checked={pendingApprovals.length > 0 && pendingApprovals.every(a => selectedIds.has(a._id))}
                  onChange={toggleSelectAll}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer" />
              )}
              Project
            </div>
            <div className="col-span-2">Requested By</div>
            {isCollab ? <div className="col-span-2">Target Dept</div> : <div className="col-span-2">Type</div>}
            <div className="col-span-2">Current Approver</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1">Urgency</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {/* Grid rows — matches py-2.5, border-b border-gray-100 pattern */}
          {filtered.map(a => {
            const currentApprover = a.approvalChain.find(i => i.status === 'pending') || a.approvalChain[0];
            const isPending = a.status === 'pending' || a.status === 'in_progress';
            return (
              <div key={a._id}
                className={`grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50/80 transition-colors ${selectedIds.has(a._id) ? 'bg-blue-50/40' : ''}`}
                onClick={() => { setSelected(a); setComments(''); }}>
                <div className="col-span-3 flex items-center gap-2 min-w-0">
                  {isPending && (
                    <input type="checkbox"
                      checked={selectedIds.has(a._id)}
                      onChange={() => toggleSelect(a._id)}
                      onClick={e => e.stopPropagation()}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer shrink-0" />
                  )}
                  {a.requiresHigherApproval && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">ESC</span>
                  )}
                  <span className="text-sm text-gray-700 truncate">{a.projectName}</span>
                </div>
                <div className="col-span-2 flex items-center min-w-0">
                  <div>
                    <div className="text-sm text-gray-700 truncate">{a.requestedByName}</div>
                    <div className="text-xs text-gray-400 truncate">{a.requestedDepartmentName}</div>
                  </div>
                </div>
                {isCollab ? (
                  <div className="col-span-2 flex items-center text-sm text-gray-500 truncate">{a.targetDepartmentName || '—'}</div>
                ) : (
                  <div className="col-span-2 flex items-center">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{a.approvalType.replace('_', ' ')}</span>
                  </div>
                )}
                <div className="col-span-2 flex items-center min-w-0">
                  <div>
                    <div className="text-sm text-gray-700 truncate">{currentApprover?.approverName || '—'}</div>
                    <div className="text-xs text-gray-400 truncate">{currentApprover?.approverRoleName || ''}</div>
                  </div>
                </div>
                <div className="col-span-1 flex items-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_PILL[a.status] || 'bg-gray-100 text-gray-500'}`}>
                    {a.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="col-span-1 flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${URGENCY_DOT[a.urgency] || 'bg-gray-400'}`} />
                  <span className="text-xs text-gray-500">{a.urgency}</span>
                </div>
                <div className="col-span-1 flex items-center justify-end">
                  <span className="text-xs text-blue-600 font-medium">{isPending ? 'Review' : 'View'}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal — matches RoleManagement modal pattern exactly */}
      {selected && !showEscalate && (
        <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden border border-gray-200" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
              <span className="text-sm font-semibold text-gray-900">{selected.projectName}</span>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Requested By</label>
                  <p className="text-sm text-gray-700">{selected.requestedByName}</p>
                  <p className="text-xs text-gray-400">{selected.requestedDepartmentName}</p>
                </div>
                {isCollab && selected.targetDepartmentName && (
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Target Department</label>
                    <p className="text-sm text-gray-700">{selected.targetDepartmentName}</p>
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Urgency</label>
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${URGENCY_DOT[selected.urgency] || 'bg-gray-400'}`} />
                    <span className="text-sm text-gray-700 capitalize">{selected.urgency}</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Status</label>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_PILL[selected.status] || 'bg-gray-100'}`}>{selected.status.replace('_', ' ')}</span>
                </div>
              </div>

              {selected.requestReason && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="text-xs text-gray-500 mb-1 block">Reason</label>
                  <p className="text-sm text-gray-700">{selected.requestReason}</p>
                </div>
              )}

              {/* Approval chain */}
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">Approval Chain</label>
                <div className="border border-gray-200 rounded-lg p-3 space-y-1.5">
                  {selected.approvalChain.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${STATUS_PILL[item.status] || 'bg-gray-100 text-gray-500'}`}>{item.status}</span>
                      <span className="font-medium text-gray-700">{item.approverName}</span>
                      <span className="text-gray-400">{item.approverRoleName}</span>
                      {item.forwardedToName && <span className="text-gray-500 ml-auto shrink-0">→ {item.forwardedToName}</span>}
                      {item.comments && <span className="text-gray-400 italic ml-auto truncate max-w-[160px]" title={item.comments}>"{item.comments}"</span>}
                    </div>
                  ))}
                </div>
              </div>

              {(selected.status === 'pending' || selected.status === 'in_progress') && (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Comments</label>
                  <textarea value={comments} onChange={e => setComments(e.target.value)} rows={3}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none"
                    placeholder="Optional comments..." />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-2 justify-end shrink-0">
              {selected.shareableToken && (
                <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/approve/${selected.shareableToken}`)}
                  className="text-xs text-gray-500 px-3 py-2 hover:bg-gray-50 rounded-lg border border-gray-200 mr-auto">
                  Copy Link
                </button>
              )}
              {(selected.status === 'pending' || selected.status === 'in_progress') ? (
                <>
                  <button onClick={() => setShowEscalate(true)}
                    className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-4 py-2 rounded-lg font-medium hover:bg-amber-100 disabled:opacity-50 transition-colors" disabled={actionLoading}>
                    Escalate
                  </button>
                  <button onClick={handleReject}
                    className="text-xs text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-lg font-medium hover:bg-red-100 disabled:opacity-50 transition-colors" disabled={actionLoading}>
                    {actionLoading ? 'Processing...' : 'Reject'}
                  </button>
                  <button onClick={handleApprove}
                    className="text-xs bg-green-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors" disabled={actionLoading}>
                    {actionLoading ? 'Processing...' : 'Approve'}
                  </button>
                </>
              ) : (
                <button onClick={() => setSelected(null)} className="text-xs text-gray-500 px-4 py-2 hover:bg-gray-50 rounded-lg">Close</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Escalate Modal — matches RoleManagement smaller modal */}
      {showEscalate && selected && (
        <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowEscalate(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden border border-gray-200" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-900">Escalate to Higher Authority</span>
              <button onClick={() => setShowEscalate(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-gray-700">{selected.projectName}</span>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Reason for escalation</label>
                <textarea value={escalationReason} onChange={e => setEscalationReason(e.target.value)} rows={4}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none"
                  placeholder="Why does this need higher authority..." />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 shrink-0">
              <button onClick={() => setShowEscalate(false)} className="text-xs text-gray-500 px-4 py-2 hover:bg-gray-50 rounded-lg">Cancel</button>
              <button onClick={handleEscalate} disabled={actionLoading || !escalationReason.trim()}
                className="text-xs bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors">
                {actionLoading ? 'Escalating...' : 'Escalate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalsDashboard;
